import React from 'react'

import { useForTablet } from '@sketch/components'

import { useCanvasScope } from '../../../canvas/scope'
import { DraftAnnotationNotifier } from '../../../annotations/components'
import { SharePageCanvas } from '../../PageCanvasView/components/SharePageCanvas'
import { PageCanvasSupportGate } from '../../PageCanvasView/components/PageCanvasSupportGate'
import { CanvasLeftPanel } from '../../../canvas/components/CanvasLeftPanel'
import { StyledCanvasViewWrapper } from '../../PageCanvasView/PageCanvasView.styles'

interface CanvasSurfaceProps {
  canvasRef?: React.RefObject<HTMLDivElement | null>
}

/**
 * The canvas and the panel beside it.
 *
 * Identical across the page, Section and frame views and across both hosts,
 * because everything that varied between them is read rather than passed: the
 * page to render and the entity to isolate both come from the scope.
 *
 * The browser-support check lives here rather than in each view. It was written
 * for the page canvas and copied into every view built from it, which is why
 * the frame view - the one with a different ancestry - never had it and showed
 * a silently broken canvas where the others explained.
 */
export function CanvasSurface({ canvasRef }: CanvasSurfaceProps) {
  const scope = useCanvasScope()
  const isTabletAndBigger = useForTablet()

  // No page, nothing to draw. The frame route learns its page from the frame,
  // so this holds until that query lands; the views around it carry on.
  if (!scope?.pageUUID) {
    return null
  }

  return (
    <PageCanvasSupportGate>
      <StyledCanvasViewWrapper>
        <CanvasLeftPanel />
        <SharePageCanvas
          ref={canvasRef}
          permanentPageIdentifier={scope.pageUUID}
          // The focused entity - frame or Section - which the canvas fades the
          // rest of the page back from, and which scopes the overlays.
          focusedEntityUUID={scope.entityUUID}
        />
        {isTabletAndBigger && <DraftAnnotationNotifier />}
      </StyledCanvasViewWrapper>
    </PageCanvasSupportGate>
  )
}
